<script>
	import { navigate } from "svelte-routing";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import { damageReviewedData, errorMessage } from "src/store/store";
	import { onMount } from "svelte";

	import ConfirmModal from "../../../../components/CustomModal/ConfirmModal.svelte";
	import CustomButton from "../../../../components/CustomButton/CustomButton.svelte";
	import CustomCollapseCard from "../../../../components/CustomCollapseCard/CustomCollapseCard.svelte";
	import ErrorMessageHandler from "../../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import LoaderContainer from "src/components/Loader/LoaderContainer.svelte";
	import PageHeader from "../../../../components/PageHeader/PageHeader.svelte";
	import CompareReports from "../../../../components/CompareReports/CompareReports.svelte";
	import { convert } from "../../../../services/helpers";
	import { extractErrorMessage } from "../../../../utils/misc";
	import { loading } from "../../../../store/store";
	import CustomBreadcrumb from "src/components/CustomBreadcrumb/CustomBreadcrumb.svelte";
	import { getCaseDetails, patchCaseStatus } from "src/services/fmsTasking";
	import { getAssetOwner } from "src/services/vehicle";

	//#region Props
	export let id;
	//#endregion

	//#region Variables
	let err;
	let data = {};
	let header = "";
	let reportText = "";
	let isOpen = false;
	let assetOwner = "";

	let showDropdown = false;
	let modalSubmitHandler;

	errorMessage.subscribe((value) => {
		err = value;
	});
	//#endregion

	//#region API Calls
	/**
	 * Fetches investigation review data by the provided id.
	 * Sets the data variable with the response.
	 * Handles and logs errors.
	 *
	 * @param {void}
	 */
	async function fetchReviewbyId() {
		try {
			const res = await getCaseDetails(id);
			data = res?.data;
			return;
		} catch (error) {
			console.error(error);
			extractErrorMessage(error);
		}
	}

	/**
	 * Fetches the asset owner name for a given vehicleId.
	 * Makes an API call to retrieve vehicle details and sets the assetOwner variable.
	 *
	 *
	 * @param {string|number} vehicleId - The ID of the vehicle to fetch the asset owner for.
	 */
	async function fetchAssetOwner(vehicleId) {
		try {
			const res = await getAssetOwner(vehicleId);
			assetOwner = res.data.assetOwnerName;
		} catch (error) {
			console.error(error);
			extractErrorMessage(error);
		}
	}

	//#endregion

	//#region View Functions
	/**
	 * Closes the modal.
	 * Navigates to the damage validation page for the current vehicle and closes the modal.
	 *
	 * @param {void}
	 */
	function handleSubmit() {
		if (data?.vehicleId) {
			isOpen = false; // close the modal
			navigate(
				`/issues/case-management/investigate-damage-validation?vehicleId=${data.vehicleId}&id=${id}`
			);
		}
	}

	async function handleReject(reason) {
		try {
			const res = await patchCaseStatus(id, {
				status: "Rejected",
				reason: reason,
			});
			if (res.status === 200) {
				isOpen = false;
				navigate("/issues/case-management?tab=Rejected");
			}
		} catch (err) {
			console.log(err);
			extractErrorMessage(err);
		}
	}

	/**
	 * Toggles the visibility of the confirmation modal and sets its content.
	 *
	 * @param {string} headerText - The text to display as the modal's header.
	 * @param {string} text - The main content or message to display in the modal.
	 * @param {boolean} withDropdown - Whether to show a dropdown in the modal.
	 * @param {function} submitHandler - The function to call when the modal's confirm button is clicked.
	 */
	function toggleModal(headerText, text, withDropdown, submitHandler) {
		isOpen = true;
		header = headerText;
		reportText = text;
		showDropdown = withDropdown;
		modalSubmitHandler = submitHandler;
	}

	/*
		onMount lifecycle hook:
		- Sets loading state to true
		- Fetches investigation review data by ID
		- If vehicleId exists, fetches asset owner
		- Handles errors and resets loading state when done
	*/
	onMount(async () => {
		loading.set(true);
		try {
			await fetchReviewbyId();
			if (data?.vehicleId) {
				await fetchAssetOwner(data.vehicleId);
			}
		} catch (e) {
			console.error("Failed to load data:", e);
		} finally {
			loading.set(false);
		}
	});
</script>

<LoaderContainer>
	<CustomBreadcrumb
		links={[
			{ label: "Case Management", to: "/issues/case-management" },
			{ label: data.plateNo },
		]}
	/>
	<Row class="max-height">
		<Col xs={12}>
			<div>
				<ErrorMessageHandler message={err} />
				<Col style="margin-bottom: 24px;">
					<PageHeader
						title={data.plateNo}
						titleStatusBar="Investigating"
					/>
				</Col>

				<!-- #region Vehicle Information -->
				<div class="mb-4">
					<CustomCollapseCard
						title="Vehicle Information"
						isOpenDefault={true}
					>
						<div class="p-0">
							<div class="row">
								<div class="col">
									<div class="description">
										Vehicle Number
									</div>
									<div class="value">{data?.plateNo}</div>
								</div>
								<div class="col">
									<div class="description">Assets Owner</div>
									<div class="value">
										{assetOwner}
									</div>
								</div>
							</div>

							<hr class="divider" />

							<div class="row">
								<div class="col">
									<div class="description">Make</div>
									<div class="value">
										{data?.vehicleInfo?.make}
									</div>
								</div>
								<div class="col">
									<div class="description">Model</div>
									<div class="value">
										{data?.vehicleInfo?.model}
									</div>
								</div>
							</div>
						</div>
					</CustomCollapseCard>
				</div>
				<!-- #endregion -->

				<!-- #region Damage Information -->
				<div class="mb-5">
					<CustomCollapseCard
						title="Reviewer Information"
						isOpenDefault={true}
					>
						<div class="p-0">
							<div class="row">
								<div class="col">
									<div class="description">Reviewer Name</div>
									<div class="value">
										{data?.lastReviewedBy}
									</div>
								</div>
								<div class="col">
									<div class="description">
										Last Reviewed Date
									</div>
									<div class="value">
										{convert(data?.lastReviewedAt)}
									</div>
								</div>
							</div>
						</div>
					</CustomCollapseCard>
				</div>
				<!-- #endregion -->

				<!-- #region Damage Investigation Card -->
				<div class="d-flex flex-row max-height report-container">
					<CompareReports
						caseId={id}
						state="edit"
						caseManagement={data?.caseOrigin === "Desmond"}
					/>
				</div>
				<!-- #endregion -->

				<!-- #region Action Buttons -->
				<!-- Reject button -->
				<div class="d-flex flex-row justify-content-end my-1">
					<div class="mx-1">
						<CustomButton
							buttonText="Reject"
							on:click={() =>
								toggleModal(
									"Reject Report",
									"You are about to declare that this vehicle do not have any new damages as of the last report. Please indicate a reason.",
									true,
									handleReject
								)}
						/>
					</div>

					<!-- Submit button -->
					<div class="mx-1">
						<CustomButton
							buttonText="Submit Investigation"
							color="custom-blue"
							disabled={$damageReviewedData.length === 0}
							on:click={() =>
								toggleModal(
									"Submit Investigations",
									"You are about to submit these investigations and proceed to the quotation phase. Are you sure you want to proceed?",
									false, // no dropdown for submit investigation modal
									handleSubmit
								)}
						/>
					</div>
				</div>
				<!-- #endregion -->
			</div>
		</Col>
	</Row>
</LoaderContainer>
<ConfirmModal
	open={isOpen}
	toggle={() => (isOpen = false)}
	{header}
	bodyText={reportText}
	buttonColor="primary"
	buttonText="Confirm"
	outline={false}
	handleSubmit={modalSubmitHandler}
	{showDropdown}
	dropdownOptions={[
		{ value: "NoNewDamage", label: "No new damage" },
		{ value: "NoDamage", label: "No damage" },
	]}
/>

<style>
	:global(.max-height) {
		height: fit-content;
	}

	:global(.divider) {
		border: 0.5px solid var(--fds-on-surface-variant);
		margin: 24px 0;
	}

	.description {
		color: var(--fds-on-surface-variant);
	}

	.report-container {
		margin-bottom: 24px;
	}

	.last-reviewed-badge {
		position: absolute;
		top: -16px;
		left: 0;
		background-color: #248a3d; /* green */
		color: white;
		font-weight: 700;
		font-size: 16px;
		padding: 5px 18px;
		border-radius: 4px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		z-index: 1;
	}
</style>
